import { useCart } from '@shopify/hydrogen-react'
import { Money } from '@shopify/hydrogen-react'
import Image from 'next/image'
import { useMemo } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { AspectRatio } from '@/components/primitives/AspectRatio/AspectRatio'
import { Quantity } from '@/components/primitives/Quantity/Quantity'
import { toCurrency } from '@/lib/formatPrice'

import { IMAGE_SIZE, CART_DEBOUNCE_TIME } from './Cart.constants'

import type { CartLineType } from './Cart.types'
import type { AttributeInput } from '@shopify/hydrogen-react/storefront-api-types'

// * LPG Line Items have rental prices associated to it, this line_item gets rendered if a user adds an LPG item from the /lpg-45kg product page.
// * LPG Stockholding Items gets rendered as RentalLineItems.
export const PdpLPGCartLineItem = ({ line }: { line: CartLineType }) => {
  const cart = useCart()

  const rentalPrice = Number(line?.attributes?.find((attribute) => attribute?.key === '_rental_price')?.value)
  const hireQuantity = Number(line?.attributes?.find((attribute) => attribute?.key === '_hire_quantity')?.value)
  const totalPrice = Number(line?.cost?.totalAmount?.amount)
  const subTotalPrice = Number(line?.cost?.subtotalAmount?.amount)
  const quantity = Number(line?.quantity)

  const isDiscounted = totalPrice < subTotalPrice
  const totalRentalPrice = rentalPrice * quantity
  const hasRentalPrice = rentalPrice > 0 && hireQuantity > 0

  const displayHireQuantity = useMemo(() => {
    if (hireQuantity === 0) return 0

    return quantity
  }, [quantity, hireQuantity])

  const handleHireQuantityChange = useDebouncedCallback((quantity: number) => {
    if (!line || !line.id || !line.attributes || !line.attributes.length) return
    const attributes = line.attributes.filter((attribute) => attribute && attribute.key && attribute.value) as AttributeInput[]
    const dehireQuantity = Number(attributes.find((attribute) => attribute.key === '_dehire_quantity')?.value || '0')

    if (dehireQuantity === 0 && quantity === 0) {
      cart?.linesRemove([line.id])

      return
    }

    attributes.forEach((attribute) => {
      if (attribute.key === '_hire_quantity') {
        attribute.value = quantity.toString()
      }
    })

    // If we are changing quantity >= 1, the quantity and _hire_quantity should be the same
    if (quantity >= 1) {
      cart?.linesUpdate([
        {
          id: line.id,
          quantity,
          attributes,
        },
      ])
    } else {
      // If we are changing quantity to 0, the quantity should be 1 and _hire_quantity should be 0
      cart?.linesUpdate([
        {
          id: line.id,
          quantity: 1,
          attributes,
        },
      ])
    }
  }, CART_DEBOUNCE_TIME)

  if (!line || !quantity) return <></>

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4">
        {line?.merchandise?.image?.url && (
          <div className="size-14">
            <AspectRatio ratio="1/1">
              <div className="relative size-full overflow-hidden rounded-sm border border-grey-300">
                <Image
                  src={line.merchandise.image.url}
                  alt={line.merchandise.image.altText || line.merchandise.title || ''}
                  fill
                  sizes={IMAGE_SIZE}
                  className="object-cover object-center"
                />
              </div>
            </AspectRatio>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div>
            <p className="font-medium">{line?.merchandise?.product?.title}</p>
            <div className="flex items-center gap-1">
              {line?.cost?.totalAmount && <Money data={line?.cost?.totalAmount} />}
              {line?.cost?.subtotalAmount && hireQuantity > 0 && isDiscounted && <Money className="text-grey-400 line-through" data={line?.cost?.subtotalAmount} />}
            </div>
          </div>

          {hasRentalPrice && <p className="text-xs text-grey-700">Includes annual rental fee of {toCurrency({ value: totalRentalPrice })}</p>}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-grey-700">Order QTY</p>
        <Quantity value={displayHireQuantity} onChange={handleHireQuantityChange} maxNumber={2} />
      </div>
    </div>
  )
}
